/**
 * Validates Promise2Protect business rules based on education qualification, income, occupation, and sum assured
 * @param {Object} conditionState - The condition state containing all form field values
 * @param {string} pageId - Current page ID (e.g., "PageP2PCalculatePremium")
 * @param {string} personalDetailsSectionId - Personal details section ID (e.g., "P2PCPPersonalDetailsSection")
 * @param {string} occupationSectionId - Occupation section ID (e.g., "P2PCPResidentAndOccupationDetailSection")
 * @param {string} channelCode - Current channel code (e.g., "PBP", "WEBSITE")
 * @param {Object} businessRules - Business rules configuration object from database
 * @returns {Object} - Validation result with errors and freeze instructions
 *   - errors: Object with formControlKey as keys and error messages as values
 *   - shouldFreezeOccupation: Boolean indicating if occupation should be frozen
 *   - frozenOccupationValue: String value to freeze occupation to (if applicable)
 */
export const validateP2PBusinessRules = (
  conditionState,
  pageId,
  personalDetailsSectionId,
  occupationSectionId,
  channelCode,
  businessRules
) => {
  const errors = {}
  let shouldFreezeOccupation = false
  let frozenOccupationValue = null

  try {
    // If business rules config is not provided, skip validation
    if (!businessRules) {
      return { errors, shouldFreezeOccupation, frozenOccupationValue }
    }

    // Check if channel code is in the allowed list
    // If channelCode is not provided or not in the list, skip validation
    if (!channelCode || !businessRules.channelCode || !businessRules.channelCode.includes(channelCode)) {
      return { errors, shouldFreezeOccupation, frozenOccupationValue }
    }
    // Extract form values from conditionState
    const educationalQualification = conditionState[
      `${pageId}.${personalDetailsSectionId}.educationalQualification`
    ]
    const annualIncome = conditionState[
      `${pageId}.${personalDetailsSectionId}.AnnualIncome`
    ]
    const occupationType = conditionState[
      `${pageId}.${occupationSectionId}.CPResidentAndOccupationType`
    ]
    const isNiftyOrGovEmployee = conditionState[
      `${pageId}.${personalDetailsSectionId}.CFAreYouNiftyOrGovEmployee`
    ]
    // Check sumAssured in multiple possible locations
    const sumAssured = conditionState[
      `${pageId}.${occupationSectionId}.sumAssured`
    ] || conditionState[`${pageId}.${personalDetailsSectionId}.sumAssured`] 
    || conditionState[`${pageId}.P2PCPCustomizePlanSection.sumAssured`]

    // If required fields are missing, skip validation
    // Note: For Nifty500/Gov Employee validation, we don't need occupationType
    if (!educationalQualification || !annualIncome) {
      return { errors, shouldFreezeOccupation, frozenOccupationValue }
    }

    // Convert annual income to number if it's a string
    const annualIncomeNum = typeof annualIncome === 'string' 
      ? parseFloat(annualIncome.replace(/,/g, '')) 
      : parseFloat(annualIncome) || 0

    // Convert sum assured to number if it exists
    const sumAssuredNum = sumAssured 
      ? (typeof sumAssured === 'string' 
          ? parseFloat(sumAssured.replace(/,/g, '')) 
          : parseFloat(sumAssured) || 0)
      : null

    // Find the applicable rule based on education qualification
    let applicableRule = null
    let ruleKey = null

    // Check each rule category
    for (const [key, rule] of Object.entries(businessRules.rules)) {
      if (rule.educationValues.includes(educationalQualification)) {
        applicableRule = rule
        ruleKey = key
        break
      }
    }

    // If no rule found, return no errors
    if (!applicableRule) {
      return { errors, shouldFreezeOccupation, frozenOccupationValue }
    }

    // Handle Nifty500/Gov Employee special rules first (highest priority)
    if (applicableRule.nifty500GovEmployee?.enabled && isNiftyOrGovEmployee === "Y") {
      const niftyRule = applicableRule.nifty500GovEmployee
      
      // Check income threshold for Nifty500/Gov Employee
      // Error should be shown below AnnualIncome field when income is below threshold
      if (annualIncomeNum < niftyRule.minAnnualIncome) {
        errors["AnnualIncome"] = businessRules.errorMessage
        return { errors, shouldFreezeOccupation, frozenOccupationValue }
      }

      // If income meets threshold and freeze is enabled, freeze occupation to Salaried
      if (annualIncomeNum >= niftyRule.minAnnualIncome && niftyRule.freezeOccupation) {
        shouldFreezeOccupation = true
        frozenOccupationValue = niftyRule.frozenOccupationValue
        // Don't validate occupation type since it will be frozen to Salaried
        // Continue to check sum assured if applicable
      } else {
        // If income meets threshold but no freeze, validate normal occupation rules
        // Only validate if occupationType is selected
        if (occupationType) {
          validateOccupationRules(
            applicableRule,
            occupationType,
            annualIncomeNum,
            errors,
            businessRules.errorMessage
          )
        }
      }
    } else if (isNiftyOrGovEmployee === "N") {
      // NFT "No": Early income validation on personal details (first page)
      // Min income by education: 10th/Diploma 10 LPA, 12th 8 LPA, Graduate/Post Graduate 6 LPA
      const minIncomeWhenNiftyNo = applicableRule.minIncomeWhenNiftyNo ??
        applicableRule.occupationRules?.salaried?.minAnnualIncome
      if (minIncomeWhenNiftyNo != null && annualIncomeNum < minIncomeWhenNiftyNo) {
        errors["AnnualIncome"] = businessRules.errorMessage
        return { errors, shouldFreezeOccupation, frozenOccupationValue }
      }
      // If income passes, validate occupation rules when on occupation page
      if (occupationType) {
        validateOccupationRules(
          applicableRule,
          occupationType,
          annualIncomeNum,
          errors,
          businessRules.errorMessage
        )
      }
    } else {
      // NFT not Y/N (e.g. undefined): validate occupation rules only when occupationType selected
      if (occupationType) {
        validateOccupationRules(
          applicableRule,
          occupationType,
          annualIncomeNum,
          errors,
          businessRules.errorMessage
        )
      }
    }

    // Validate sum assured maximum if applicable
    if (applicableRule.sumAssured?.maxValidation && sumAssuredNum !== null) {
      if (sumAssuredNum > applicableRule.sumAssured.maxSumAssured) {
        errors["sumAssured"] = `Maximum Sum Assured allowed is ${applicableRule.sumAssured.maxSumAssuredDisplay}`
      }
    }

  } catch (error) {
    console.error("Error in validateP2PBusinessRules:", error)
  }

  return { errors, shouldFreezeOccupation, frozenOccupationValue }
}

/**
 * Validates occupation-specific income rules
 * @param {Object} rule - The applicable business rule
 * @param {string} occupationType - Occupation type ("1" for Salaried, "3" for Self-Employed)
 * @param {number} annualIncome - Annual income value
 * @param {Object} errors - Errors object to populate
 */
const validateOccupationRules = (rule, occupationType, annualIncome, errors, errorMessage) => {
  if (occupationType === "1") {
    // Salaried
    const salariedRule = rule.occupationRules.salaried
    if (annualIncome < salariedRule.minAnnualIncome) {
      errors["CPResidentAndOccupationType"] = errorMessage
    }
  } else if (occupationType === "3") {
    // Self-Employed
    const selfEmployedRule = rule.occupationRules.selfEmployed
    if (annualIncome < selfEmployedRule.minAnnualIncome) {
      errors["CPResidentAndOccupationType"] = errorMessage
    }
  }
}

